import { useEffect, useState } from "react";
import { getMyBranding } from "../api";

/**
 * useBranding — V5 Features 5.1 + 5.2 + 5.3
 *
 * Resolves the active brand for the current user/host. Backend already
 * picks custom-domain → team_id → default in that order, so we just
 * call /branding/me and trust the result.
 *
 * The hook applies side effects automatically:
 *   - Sets document.title to "<displayName> CRM"
 *   - Sets favicon if branding.favicon_url is provided
 *   - Sets CSS custom properties --rf-primary / --rf-accent / --rf-navy
 *     on :root so any component can read them
 *
 * Returns the branding object (or null while loading).
 *
 * Cached in sessionStorage so navigation doesn't refetch needlessly.
 */
const CACHE_KEY = "rf_branding_v1";

export default function useBranding() {
  const [branding, setBranding] = useState(() => {
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });

  useEffect(() => {
    let mounted = true;
    getMyBranding()
      .then((b) => {
        if (!mounted || !b) return;
        setBranding(b);
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(b)); } catch { /* ignore */ }
      })
      .catch(() => { /* fall back to whatever cache had */ });
    return () => { mounted = false; };
  }, []);

  // Apply side effects whenever branding changes
  useEffect(() => {
    if (!branding) return;

    // Document title
    if (branding.display_name && branding.display_name !== "RealtyFlows") {
      document.title = `${branding.display_name} CRM`;
    }

    // Favicon
    if (branding.favicon_url) {
      const existing = document.querySelector("link[rel='icon']");
      const link = existing || document.createElement("link");
      link.rel = "icon";
      link.href = branding.favicon_url;
      if (!existing) document.head.appendChild(link);
    }

    // CSS variables for theme overrides
    const root = document.documentElement;
    if (branding.primary_color) root.style.setProperty("--rf-primary", branding.primary_color);
    if (branding.accent_color)  root.style.setProperty("--rf-accent", branding.accent_color);
    if (branding.navy_color)    root.style.setProperty("--rf-navy", branding.navy_color);
  }, [branding]);

  return branding;
}
